<script setup lang="ts">
import { Primitive, ProgressIndicator, ProgressRoot } from 'reka-ui';
import { computed } from 'vue';

import { useDataTestid } from '@/kasten-components/composables';

import {
  progressBaseVariants,
  progressIndicatorVariants,
  progressRootVariants,
} from './progress-variants';
import type { KaProgressProps } from './types';
export type { KaProgressProps } from './types';

defineOptions({ name: 'KaProgress' });

const props = withDefaults(defineProps<KaProgressProps>(), {
  as: 'div',
  size: 'md',
  color: 'primary',
  value: 0,
});

const { dataTestid } = useDataTestid();

// Ensure value is between 0 and 100 and handle invalid values
const normalizedValue = computed(() => {
  const value = props.value;
  // Handle undefined, null, NaN, or invalid values
  if (value == null || isNaN(value)) {
    return 0;
  }
  return Math.min(100, Math.max(0, value));
});
</script>

<template>
  <Primitive
    :as="props.as"
    :data-testid="dataTestid"
    :class="progressRootVariants({ size: props.size, class: props.class })"
  >
    <ProgressRoot v-model="normalizedValue" :class="progressBaseVariants()">
      <ProgressIndicator
        :class="progressIndicatorVariants({ color: props.color })"
        :style="`transform: translateX(-${100 - normalizedValue}%)`"
        data-testid="progress-indicator"
      />
    </ProgressRoot>
  </Primitive>
</template>
